import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { homeApi } from '../api/api';
import { parseYoutubeId } from '../utils/youtube';
import SocialLink from './ui/SocialLink';
import { DEFAULT_SOCIAL_URLS } from '../utils/socialLinks';

const FALLBACK = {
  isLive: true,
  url: `${DEFAULT_SOCIAL_URLS.youtube}/live`,
  videoId: '',
  label: null, // use i18n when null
};

/**
 * Floating live widget. Visibility + URL come from admin Home settings.
 * Browsers only allow autoplay when muted; sound works on YouTube after click.
 */
function LiveStreamButton() {
  const { t } = useTranslation();
  const [config, setConfig] = useState(FALLBACK);
  const [ready, setReady] = useState(false);
  const [showPlayer, setShowPlayer] = useState(true);

  useEffect(() => {
    homeApi
      .getAll()
      .then((res) => {
        if (res.success && res.data?.live_stream) {
          const ls = res.data.live_stream;
          const url = (ls.url || '').trim() || FALLBACK.url;
          const apiLabel = (ls.label || '').trim();
          setConfig({
            isLive: Boolean(ls.isLive),
            url,
            videoId: ls.videoId || parseYoutubeId(url) || FALLBACK.videoId,
            // Keep admin label only if custom; default English falls back to i18n
            label: apiLabel && apiLabel !== 'Live Katha' ? apiLabel : null,
          });
        }
      })
      .catch(() => { /* keep fallback */ })
      .finally(() => setReady(true));
  }, []);

  if (!ready) return null;
  if (!config.isLive || !config.url) return null;

  const videoId = config.videoId || parseYoutubeId(config.url);
  const embedSrc = videoId
    ? `https://www.youtube.com/embed/${videoId}?autoplay=1&mute=1&controls=0&rel=0&modestbranding=1&playsinline=1&disablekb=1&fs=0&iv_load_policy=3`
    : null;

  const label = config.label || t('live.label');
  const watchAria = t('live.watch_aria', { label });

  return (
    <div className="live-stream-widget">
      {showPlayer && embedSrc && (
        <div className="live-stream-player">
          <button
            type="button"
            className="live-stream-widget__close"
            onClick={() => setShowPlayer(false)}
            aria-label={t('live.close_aria')}
            title={t('live.close')}
          >
            <i className="fas fa-times"></i>
          </button>
          <div className="live-stream-player__frame">
            <iframe
              className="live-stream-player__iframe"
              src={embedSrc}
              title={label}
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
              allowFullScreen
            />
            <span className="live-stream-player__live">
              <span className="live-stream-player__live-dot" aria-hidden="true"></span>
              {t('live.badge')}
            </span>
            <SocialLink
              href={config.url}
              className="live-stream-player__hit"
              aria-label={watchAria}
            />
          </div>
        </div>
      )}

      <SocialLink
        href={config.url}
        className="live-stream-btn"
        aria-label={watchAria}
        title={label}
      >
        <span className="live-stream-btn__body">
          <span className="live-stream-btn__badge">
            <span className="live-stream-btn__dot" aria-hidden="true"></span>
            {t('live.badge')}
          </span>
          <span className="live-stream-btn__copy">
            <span className="live-stream-btn__title">{label}</span>
            <span className="live-stream-btn__sub">{t('live.watch_now')}</span>
          </span>
          <i className="fab fa-youtube live-stream-btn__icon" aria-hidden="true"></i>
        </span>
      </SocialLink>
    </div>
  );
}

export default LiveStreamButton;
